import React from 'react';
import PropTypes from 'prop-types';
import { JsonLd } from 'react-schemaorg';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import lowerCase from 'lodash/lowerCase';
import { I18nHoc } from 'helpers/i18n';
import absoluteUrl from 'helpers/absoluteUrl';
import numDesc, { humanizeBigNumbers, numDescOnlyText } from 'helpers/numDesc';
import { FavoritesButton } from 'subsys/favorites';
import { AbstractObjectTile, TileView } from 'components/layouts';
import InteractiveComponent from 'components/interactive-fly-component';
import Row, { Col } from 'components/row';
import { iconTypes } from 'components/icon';
import Link from 'components/link';

import { ModalCallBack } from 'src-sites/bild/section-project/contact-to-developer';
import { getAskDevHeader } from 'src-sites/bild/section-project/render-common';

import linksDomik from 'src-sites/domik/links';

import { checkSold, renderPricePerMeter } from './list-card';
import { mergePrice } from '../render-common';

import translates from '../../section-projects/card-i18n';

const arabic = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
}

@I18nHoc(translates())
export default class TileCardObject extends AbstractObjectTile {
  static displayName = 'TileCardObject';

  static propTypes = {
    showLCName: PropTypes.bool,
    projectData: PropTypes.shape({}),
    ...AbstractObjectTile.propTypes
  };

  constructor(props, cfg) {
    super(props, cfg);
    this.renderSuperImage = this.renderSuperImage.bind(this);
  }

  renderSuperImage(...args) {
    return super.renderImage(...args);
  }

  checkForEmptyData(data) {
    if (this.props.checkForEmptyData) return this.props.checkForEmptyData(data);

    const price = mergePrice(data);
    const area = get(data, 'object_properties.all_area') ||
      get(data, 'object_properties.land_area');

    return (
      !area &&
      !price.mainPrice
    );
  }

  renderImage(data, bemClasses) {
    // if (!this.state.clientSide) return null;

    const { i18n } = this;
    const { projectData } = this.props;
    const typeObject = get(data, 'classesDict.value_long') || get(data, 'type_dict.entity_long') || get(data, 'classes') || '';
    const areaLand = get(data, 'object_properties.land_area') && numDesc(get(data, 'object_properties.land_area'), get(data, 'object_properties.land_area_type.F1'), get(data, 'object_properties.land_area_type.F2'), get(data, 'object_properties.land_area_type.F3'));
    const areaPerMeter = `${get(data, 'object_properties.all_area')} м²`;
    const areaObject = areaLand || areaPerMeter;
    const header = get(data, 'complex_display_name') || get(data, 'house_properties.complex_name');
    const priceObject = get(data, 'price');
    const pricePerMeterFunc = renderPricePerMeter(priceObject, bemClasses, true);
    const pricePerMeter = pricePerMeterFunc ? `${i18n('textFrom')} ${pricePerMeterFunc}` : i18n('defaultPriceText');
    const locality = get(projectData, 'locality_display_name') || get(data, 'location_path');
    const alt = `${typeObject} ${areaObject} в ${header} ${pricePerMeter}, ${locality}`;
    const image = get(data, 'title_image.id');

    return (
      <>
        {super.renderImage(data, bemClasses, false, false, false, { alt })}
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            name: alt || null,
            description: alt || null,
            contentLocation: get(projectData, 'parent_display_path', null),
            contentUrl: image ? absoluteUrl(`/images/thumb/full/${image}`) : null
          }}
        />
      </>
    );
  }

  renderTileLink =(bemClasses, data)=> {
    const { i18n } = this;
    const { all_flats_sold_object, $entity, price, url } = data;
    const soldData = this.props.soldData;
    const sold = checkSold.call(this, all_flats_sold_object, soldData, $entity);
    const bottomSoldTxt = sold.sold ? 'продано' : null;
    const all_area = data.object_properties && data.object_properties.all_area;

    const land_area = get(data, 'object_properties.land_area');
    const land_area_type = get(data, 'object_properties.land_area_type') || {};
    const landArea = land_area_type.id === 2 ? land_area / 100 : land_area;
    const area = all_area || landArea;
    const areaType = !all_area && land_area && land_area_type ?
      numDescOnlyText(
        area,
        land_area_type.F1 || '',
        land_area_type.F2 || '',
        land_area_type.F3 || ''
      ) :
      'м²';

    if (!price) return null;

    const title = price.mainPrice ?
      `${humanizeBigNumbers.call(this, price.mainPrice)}\u00a0${lowerCase(price.currency)}\u0020за\u00a0${all_area}\u00a0м²` :
      i18n('defaultPriceText');
    const priceText = price.mainPrice ?
      `${humanizeBigNumbers.call(this, price.mainPrice)}\u00a0${price.currency}` :
      i18n('defaultPriceText');

    return (
      <Link
        {...bemClasses('link', 'price')}
        href={url}
        title={title}
        blank
      >
        <span {...bemClasses('linkContent')}>
          <span {...bemClasses('text', { price: true, sold: sold.sold })} children={bottomSoldTxt || priceText} />
          {area &&
            <span {...bemClasses('text', { top: true, sold: sold.sold })} children={`${area}\u00a0${areaType}`} />
          }
        </span>
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'Offer',
            price: price.mainPrice || i18n('defaultPriceText'),
            priceCurrency: price.name_standart || null,
            availability: sold.sold ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock'
          }}
        />
      </Link>
    );
  };

  renderContent(data, bemClasses) {
    const { i18n } = this;

    if (this.checkForEmptyData(data)) return <span {...bemClasses('text', 'empty')} children={i18n('noDataText')} />;

    const storey = get(data, 'object_properties.storeys');
    const entity = get(data, '$entity');
    const house_high = get(data, 'house_properties.house_high');
    const land_area = get(data, 'object_properties.land_area');
    const all_area = get(data, 'object_properties.all_area');
    const built_year = get(data, 'house_properties.built_year');
    const built_quarter = get(data, 'house_properties.built_quarter');
    const built_terms = built_year && built_quarter ?
      `${arabic[built_quarter]} квартал ${built_year}` : null;
    const complexName = get(data, 'complex_display_name');
    let storeyTxt = null;
    if (entity !== 'objectsSaleGarage') {
      if (storey && house_high) storeyTxt = `${i18n('floor')}\u0020${storey}\u00a0/\u00a0${house_high}`;
      if (!storey && house_high) storeyTxt = `${i18n('numberOfStoreys')}\u00a0${house_high}`;
    }

    const classesDict = get(data, 'classesDict');
    const apartmentName = get(classesDict, 'value_long') || get(classesDict, 'entity_long', '');
    const url = get(data, 'url');
    const images = get(data, 'images');
    const image = images && images[0] ? images[0].id : null;
    const type = get(data, 'complex.type');
    const dict = get(classesDict, 'dict');
    const apartmentValue = get(classesDict, 'combo') || get(classesDict, 'value_long') || get(classesDict, 'entity_long', 1);
    const apartmentType = 'ROM';
    const landAreaSeo = land_area * 100;
    const areaTxt = all_area || landAreaSeo;
    const areaTypeSeo = 'MTK';

    const all_flats_sold_object = get(data, 'all_flats_sold_object');
    const soldData = this.props.soldData;
    const sold = checkSold.call(this, all_flats_sold_object, soldData, entity);
    const flatsConversion = get(this, 'props.projectData.product.addition_fields.flats_conversion');
    const projectData = get(this, 'props.projectData') || {};
    const developerEmail = get(projectData, 'salers[0].contact_email');
    const askDevHeader = getAskDevHeader.call(this, bemClasses, projectData);
    const discount = get(data, 'possible_discount');
    const modalBtnLabel = discount ? i18n('aboutDiscount') : i18n('aboutAvailability');
    const modalBtnIcon = discount ? iconTypes.gift : iconTypes.phoneCalls;

    return (
      <>
        {this.props.showLCName && complexName &&
          <div {...bemClasses('location')}>
            <span {...bemClasses('text')} children={complexName} />
          </div>
        }
        <Row {...bemClasses('description', 'area')}>
          <Col allPr={0}>
            <span {...bemClasses('text', { description: true, top: true, sold: sold.sold })} children={`${i18n('price')}:`} />
          </Col>
          <Col allAuto allPr={0}>
            {built_terms &&
              <span {...bemClasses('text', { description: true, sold: sold.sold })} children={built_terms} />
            }
          </Col>
        </Row>
        {this.renderTileLink(bemClasses, data)}
        <Row {...bemClasses('description', 'floor')}>
          <Col {...bemClasses('col', 'storey')} allFlex allPr={0}>
            {storeyTxt &&
              <span {...bemClasses('text', 'description')} children={storeyTxt} />
            }
          </Col>
          <Col
            {...bemClasses('col', 'favorite')}
            allAuto
            allPr={0}
            children={this.getInteractiveComponents(bemClasses)}
          />
        </Row>
        {flatsConversion && developerEmail &&
          <div {...bemClasses('btnCallBack')}>
            <ModalCallBack
              classes={bemClasses}
              askDevHeader={askDevHeader}
              projectId={projectData.id}
              projectUrl={url}
              developer={projectData.main_developer}
              developerEmail={developerEmail}
              modalBtnProps={{
                label: modalBtnLabel,
                title: modalBtnLabel,
                inListDD: false,
                iconType: modalBtnIcon
              }}
            />
          </div>
        }
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'Apartment',
            name: apartmentName,
            url: absoluteUrl(url),
            image: absoluteUrl(`/images/thumb/full/${image}`),
            numberOfRooms: (type === 'living_complex' && dict === 'rooms') ? {
              '@type': 'QuantitativeValue',
              value: apartmentValue,
              unitCode: apartmentType
            } : null,
            floorSize: {
              '@type': 'QuantitativeValue',
              // maxValue: maxArea,
              // minValue: minArea,
              value: areaTxt,
              unitCode: areaTypeSeo
            }
          }}
        />
      </>
    );
  }

  // Render Favourite btns
  getInteractiveComponents(bemClasses) {
    const { object, favData } = this.props;
    const index = findIndex(favData, { entity_id: object.id });

    return (
      <InteractiveComponent
        {...bemClasses('favorite')}
        iconType={iconTypes.favoritesBorder}
        newIconType={iconTypes.favorites}
        destinationPoint="favoritesIcon"
      >
        <FavoritesButton
          project="domik"
          entity="object"
          entityId={object.id}
          subgroup={object.$entity}
          inFavorites={index > -1}
          counterProps={{
            action: "favoritesObjectClick",
            entity:{
              project_id: get(this, 'props.projectData.id'),
              object_id: object.id,
            }
          }}
          hideLabel
        />
      </InteractiveComponent>
    );
  }

  renderSideBlock(data, bemClasses) {
    if (!this.state.clientSide) return null;

    return (
      <>
        {super.renderSideBlock(data, bemClasses)}
      </>
    );
  }

  render() {
    const { object, ...restProps } = this.props;

    return (
      <TileView
        {...restProps}
        data={object}
        onMouseOver={this.handleHover}
        onMouseOut={this.handleUnhover}
        urlBuilder={linksDomik.sectionLanding}
        linkCard={{ filledCard: true, filledImg: false }}
        renderImage={this.renderImage}
        renderContent={this.renderContent}
        renderSideBlock={this.renderSideBlock}
      />
    );
  }
}
